/*
 * Vorschau eines Bildes ueber alle vier Ziele nebeneinander (Konzept J.8).
 *
 * WARUM NEBENEINANDER UND NICHT NACHEINANDER. Der Editor hat Reiter fuer die
 * Ziele, man kann also schon heute jedes einzeln ansehen. Was er nicht zeigt,
 * ist der Vergleich, und genau der ist die Frage beim Bauen: passt das, was am
 * PC gut aussieht, auf dem Handy noch, und ist das 7-Zoll-Panel nicht in
 * Wahrheit zu eng. Zwischen zwei Reitern hin und her zu klicken beantwortet
 * das nicht, weil man das eine nicht mehr sieht, wenn man das andere ansieht.
 *
 * WARUM OHNE GRIDSTACK. Die Zeichenflaeche braucht Gridstack fuers Anfassen.
 * Eine Vorschau fasst nichts an, und Gridstack viermal gleichzeitig zu
 * betreiben hiesse vier Instanzen, die jede eine eigene Groesse messen wollen.
 * Hier steht ein schlichtes CSS-Raster, und das ist ausserdem naeher an dem,
 * was das Panel spaeter tut: die Kotlin-App kennt kein Gridstack, sie liest
 * nur `placements`. Was hier zu sehen ist, ist also naeher am Ergebnis als die
 * Zeichenflaeche es waere.
 *
 * SCALE. All targets share ONE scale factor, so the preview stays true to
 * size: a phone is as much smaller than the PC as it really is. The targets
 * sit in a 2x2 grid (PC | Tablet 10", Tablet 7" | phone) and the scale is
 * chosen so the two widest columns fill the available width. A row of four
 * would shrink the PC to a quarter and make it unreadable. Scaling uses
 * `transform: scale()` on a box in native size, not a narrower grid: a
 * narrower grid would reflow and show a different picture than the device.
 *
 * Clicking a target opens it in the editor; the preview answers "where does
 * it look wrong", the editor is where it gets fixed.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { WidgetHost } from '../WidgetHost';
import { useOptionalPointBindingSource } from '../PointBindingProvider';
import { hmiAppearanceAttributes } from '../appearanceModel';
import type { HmiScreen, LayoutTarget, ScreenLayout, WidgetDefinition, WidgetPlacement } from '../widgets';
import type { HmiAppearance } from '../appearanceModel';
import { FreeStage } from '../panel/FreeStage';
import { ThemeScope } from '../panel/ThemeScope';
import { GRID_SPECS, LAYOUT_TARGET_ORDER, frameOfLayout, isFreeLayout } from './editorModel';
import './TargetPreview.css';

/** Breite, die ein Ziel ohne feste Breite in der Vorschau bekommt.
 *
 *  Die beiden responsiven Ziele haben absichtlich keine Breite im Modell, sie
 *  richten sich im Betrieb nach dem Fenster. Fuer einen Vergleich braucht es
 *  trotzdem eine Zahl, sonst haengt das Bild von der Fensterbreite des Autors
 *  ab und zwei Autoren sehen Verschiedenes. Genommen ist, was hier ueblich
 *  ist: ein Bueroschirm und ein Telefon. */
const FALLBACK_WIDTH_PX: Readonly<Record<LayoutTarget, number>> = {
  desktopResponsive: 1440,
  panelFixed1280x800: 1280,
  panelFixed1024x600: 1024,
  panelPortrait800x1280: 800,
  mobileResponsive: 390,
  mobileLandscape: 844,
};

/** Minimum height in rows for targets without a fixed row count, so an
 *  almost empty layout does not shrink to a strip. Responsive targets grow
 *  with their content (they scroll on the device). */
const MIN_ROWS = 4;

/** Columns of the preview grid; see "SCALE" above. */
const PREVIEW_COLUMNS = 2;

/** Must match `gap` of `.hv-tp__row` (var(--space-4)). */
const PREVIEW_GAP_PX = 16;

interface Props {
  screen: HmiScreen;
  widgets: readonly WidgetDefinition[];
  /** The project's appearance, so the preview shows what viewers see. */
  appearance?: HmiAppearance;
  /** Open this target in the editor. */
  onSelectTarget(target: LayoutTarget): void;
  onClose(): void;
}

export function TargetPreview({ screen, widgets, appearance, onSelectTarget, onClose }: Props) {
  const widgetsById = useMemo(
    () => new Map(widgets.map((w) => [w.id, w])),
    [widgets],
  );

  const rowRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementWidth(rowRef);

  // Native width of each grid column = widest target in that column.
  const columnWidths = Array.from({ length: PREVIEW_COLUMNS }, (_, col) =>
    Math.max(...LAYOUT_TARGET_ORDER
      .filter((_, i) => i % PREVIEW_COLUMNS === col)
      .map(nativeWidthOf)));
  const nativeTotal = columnWidths.reduce((a, b) => a + b, 0);
  // Never enlarge: a preview larger than the device suggests space that
  // does not exist.
  const scale = availableWidth > 0
    ? Math.min(1, (availableWidth - PREVIEW_GAP_PX * (PREVIEW_COLUMNS - 1)) / nativeTotal)
    : 0;

  return (
    <div class="hv-tp">
      <div class="hv-tp__head">
        <h3 class="hv-tp__title">Vorschau: {screen.label}</h3>
        <p class="hv-tp__hint">
          Alle Ziele im selben Maßstab, mit Live-Werten. Ein Klick auf ein Ziel
          öffnet es zum Bearbeiten.
        </p>
        <button type="button" class="hv-tp__close" onClick={onClose}>
          Schließen
        </button>
      </div>
      <div
        ref={rowRef}
        class="hv-tp__row"
        style={{ gridTemplateColumns: columnWidths.map((w) => `${Math.floor(w * scale)}px`).join(' ') }}
      >
        {scale > 0 && LAYOUT_TARGET_ORDER.map((target) => (
          <OneTarget
            key={target}
            target={target}
            screen={screen}
            widgetsById={widgetsById}
            scale={scale}
            appearance={appearance}
            onSelect={() => onSelectTarget(target)}
          />
        ))}
      </div>
    </div>
  );
}

function nativeWidthOf(target: LayoutTarget): number {
  return GRID_SPECS[target].widthPx ?? FALLBACK_WIDTH_PX[target];
}

/** Content width of an element, kept current with a ResizeObserver. */
function useElementWidth(ref: { current: HTMLElement | null }): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure once right away: the observer only reports after the next
    // layout, which a background tab may not get for a long time.
    setWidth(Math.floor(el.getBoundingClientRect().width));
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function OneTarget({
  target,
  screen,
  widgetsById,
  scale,
  appearance,
  onSelect,
}: {
  target: LayoutTarget;
  screen: HmiScreen;
  widgetsById: Map<string, WidgetDefinition>;
  scale: number;
  appearance?: HmiAppearance;
  onSelect(): void;
}) {
  const spec = GRID_SPECS[target];
  const source = useOptionalPointBindingSource();

  const layout = screen.layouts.find((l) => l.target === target) ?? null;
  const placements: Record<string, WidgetPlacement> = layout?.placements ?? {};

  if (layout && isFreeLayout(layout)) {
    return (
      <FreeTarget target={target} screen={screen} layout={layout} widgetsById={widgetsById}
                  scale={scale} appearance={appearance} onSelect={onSelect} />
    );
  }

  const nativeWidth = nativeWidthOf(target);
  // Fixed panels show exactly their rows. Responsive targets show everything
  // that is placed; cutting it off would hide what the preview is for.
  // Wie viele Widgets dieses Ziel gar nicht kennt. Das ist die Zahl, wegen der
  // man die Vorschau aufmacht: ein Bild, das am PC fertig ist und auf dem Handy
  // aus lauter unplatzierten Kacheln besteht, sieht man sonst erst am Geraet.
  const unplaced = layout === null
    ? screen.widgetIds.length
    : screen.widgetIds.filter((id) => placements[id] === undefined).length;

  // Fixed panels show exactly their rows. Responsive targets show everything,
  // including the unplaced widgets that flow below (one row each, up to
  // `columns / flowSpan` per row); cutting it off would hide what the preview
  // is for.
  const flowSpan = Math.min(3, spec.columns);
  const usedRows = Object.values(placements).reduce((m, p) => Math.max(m, p.row + p.rowSpan), 0)
    + Math.ceil(unplaced / Math.max(1, Math.floor(spec.columns / flowSpan)));
  const rows = spec.rows ?? Math.max(MIN_ROWS, usedRows);
  const nativeHeight = rows * spec.cellHeightPx;

  return (
    <figure class="hv-tp__target">
      <figcaption class="hv-tp__caption">
        <span class="hv-tp__label">{spec.label}</span>
        <span class="hv-tp__dims">
          {nativeWidth}
          {'×'}
          {nativeHeight} px
          {spec.widthPx === null && ' (angenommen)'}
        </span>
        {layout === null && (
          <span class="hv-tp__warn">Keine Anordnung für dieses Ziel</span>
        )}
        {layout !== null && unplaced > 0 && (
          <span class="hv-tp__warn">
            {unplaced} von {screen.widgetIds.length} nicht platziert
          </span>
        )}
      </figcaption>
      {/* A div with button semantics, not a <button>: the widgets inside
          render buttons of their own, and nested buttons are invalid HTML. */}
      <div
        role="button"
        tabIndex={0}
        class="hv-tp__frame"
        title={`${spec.label} bearbeiten`}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
        }}
        style={{ width: `${Math.floor(nativeWidth * scale)}px`, height: `${Math.floor(nativeHeight * scale)}px` }}
      >
        {/* `inert`: the widgets are looked at, not operated, also not via
            the keyboard. */}
        <ThemeScope appearance={appearance} class="hv-tp__theme">
        <div
          class="hv-tp__stage"
          {...hmiAppearanceAttributes(appearance)}
          inert
          style={{
            width: `${nativeWidth}px`,
            height: `${nativeHeight}px`,
            transform: `scale(${scale})`,
            gridTemplateColumns: `repeat(${spec.columns}, minmax(0, 1fr))`,
            gridAutoRows: `${spec.cellHeightPx}px`,
          }}
        >
          {screen.widgetIds.map((widgetId) => {
            const widget = widgetsById.get(widgetId);
            if (!widget) return null;
            const p = placements[widgetId];
            // Ohne Platzierung in den Fluss, nicht weglassen. Ein
            // verschwundenes Widget ist von "Id zeigt ins Leere" nicht zu
            // unterscheiden; ein Widget im Fluss ist als unplatziert erkennbar.
            const style = p
              ? {
                  gridColumn: `${p.col + 1} / span ${Math.max(1, p.colSpan)}`,
                  gridRow: `${p.row + 1} / span ${Math.max(1, p.rowSpan)}`,
                }
              : { gridColumn: `span ${flowSpan}` };
            return (
              <div
                key={widgetId}
                class={`hv-tp__cell${p ? '' : ' hv-tp__cell--unplaced'}`}
                style={style}
              >
                <WidgetHost widget={widget} source={source ?? undefined} />
              </div>
            );
          })}
        </div>
        </ThemeScope>
      </div>
    </figure>
  );
}

/** A free layout in the preview: the same FreeStage the runtime uses, in
 *  the frame's own size at the shared scale. */
function FreeTarget({
  target,
  screen,
  layout,
  widgetsById,
  scale,
  appearance,
  onSelect,
}: {
  target: LayoutTarget;
  screen: HmiScreen;
  layout: ScreenLayout;
  widgetsById: Map<string, WidgetDefinition>;
  scale: number;
  appearance?: HmiAppearance;
  onSelect(): void;
}) {
  const spec = GRID_SPECS[target];
  const frame = frameOfLayout(layout);
  const unplaced = screen.widgetIds.filter((id) => !layout.placements[id]).length;
  return (
    <figure class="hv-tp__target">
      <figcaption class="hv-tp__caption">
        <span class="hv-tp__label">{spec.label}</span>
        <span class="hv-tp__dims">{frame.width}{'×'}{frame.height} px, frei</span>
        {unplaced > 0 && (
          <span class="hv-tp__warn">{unplaced} von {screen.widgetIds.length} nicht platziert</span>
        )}
      </figcaption>
      <div
        role="button"
        tabIndex={0}
        class="hv-tp__frame"
        title={`${spec.label} bearbeiten`}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
        }}
        style={{ width: `${Math.floor(frame.width * scale)}px`, height: `${Math.floor(frame.height * scale)}px` }}
      >
        <ThemeScope appearance={appearance} class="hv-tp__theme hv-tp__theme--free" inert>
          <FreeStage layout={layout} widgetIds={screen.widgetIds} widgetsById={widgetsById} fit="contain" />
        </ThemeScope>
      </div>
    </figure>
  );
}
